import { parseISO } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import format from "date-fns/format";
import {
  CycleTimesReportSummary,
  Report,
  ReportDateFilter,
  ReportFilter,
  ReportPlotType,
  ReportProcessFilter,
  ReportSummary,
  ReportToCreateOrUpdate,
} from "@/types/Report";
import { apiClient } from "./clients";

const parseUtcDate = (dateText: string): Date => utcToZonedTime(parseISO(dateText), "UTC");

const loadReports = (customerName: string, siteId: string) =>
  apiClient.get<Report[]>(`/report/${customerName}/${siteId}`).then((response) => response.data);

const createReport = (
  customerName: string,
  siteId: string,
  payload: Omit<ReportToCreateOrUpdate, "_id">,
) =>
  apiClient
    .post<Report>(`/report/${customerName}/${siteId}`, payload)
    .then((response) => response.data);

const updateReport = (
  customerName: string,
  siteId: string,
  id: string,
  payload: Omit<ReportToCreateOrUpdate, "_id">,
) =>
  apiClient
    .put<Report>(`/report/${customerName}/${siteId}/${id}`, payload)
    .then((response) => response.data);

const deleteReport = (customerName: string, siteId: string, id: string) =>
  apiClient
    .delete<void>(`/report/${customerName}/${siteId}/${id}`)
    .then((response) => response.data);

const convertReportFiltersToQuery = (filters: ReportFilter[]) => {
  type QueryValue = string | string[] | number[] | undefined;
  const converters: Partial<{
    [T in ReportFilter as T["type"]]: (filter: T) => Record<string, QueryValue>;
  }> = {
    daterange: ({ value }: ReportDateFilter) => {
      return {
        start_date:
          typeof value.start_date === "number"
            ? format(new Date().getTime() - value["start_date"], "yyyy-MM-dd")
            : value.start_date,
        end_date: value.end_date,
      };
    },
    processes: ({ value }: ReportProcessFilter) => {
      if (Array.isArray(value)) {
        return { processes: value };
      }
      if (value.mode === "single_process") {
        return { processes: value.single_processes };
      }
      return { processes: value.components.flatMap((component) => component.processes) };
    },
  };

  const filtersQuery = filters.reduce((acc, filter) => {
    const converter = converters[filter.type] as (
      filter: ReportFilter,
    ) => Record<string, QueryValue>;
    const query = converter
      ? converter(filter)
      : { [filter.type]: filter.value as string | string[] };
    return { ...acc, ...query };
  }, {} as Record<string, QueryValue>);

  return Object.fromEntries(Object.entries(filtersQuery).filter(([, value]) => value));
};

const mapReportSummary = (
  plotType: ReportPlotType,
  reportSummary: ReportSummary<string>,
): ReportSummary => {
  if (plotType === "cycle_times") {
    const data = reportSummary as CycleTimesReportSummary<string>;
    return data.map((item) => ({
      ...item,
      processes: item.processes.map((process) => ({
        ...process,
        start_time: parseUtcDate(process.start_time),
        end_time: parseUtcDate(process.end_time),
        work_intervals: process.work_intervals.map((workInterval) => ({
          ...workInterval,
          start_time: parseUtcDate(workInterval.start_time),
          end_time: parseUtcDate(workInterval.end_time),
        })),
      })),
      planned_event: {
        ...item.planned_event,
        start: parseUtcDate(item.planned_event.start),
        end: parseUtcDate(item.planned_event.end),
      },
    }));
  }
  return reportSummary as ReportSummary;
};

const loadReportSummary = (
  customerName: string,
  siteId: string,
  plotType: ReportPlotType,
  filters: ReportFilter[],
  { fresh }: { fresh?: boolean } = {},
): Promise<ReportSummary> =>
  apiClient
    .get<ReportSummary<string>>(
      `/report/${plotType}/${customerName}/${siteId}${fresh ? "/fresh" : ""}`,
      {
        params: convertReportFiltersToQuery(filters),
      },
    )
    .then((response) => mapReportSummary(plotType, response.data));

export default {
  loadReports,
  createReport,
  updateReport,
  deleteReport,
  loadReportSummary,
};
